import * as React from 'react'

import { useHistory } from 'react-router'

import { useAppRoutes } from '@webapp/hooks'
import { useSetOrderRequestFlowPreferencePresenter } from '@webapp/platform/iam'
import { useConversation } from './ConversationProvider'
import { useCart, type UseCartResult } from '../hooks/useCart'
import { useRecentConversations } from '../hooks/useRecentConversations'
import { type ConversationCartComponent } from '../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../value-objects/enums'
import { type OrderRequestRedirectState } from '../value-objects/OrderRequestRedirectState'

export type CartContextValue = UseCartResult

export const CartContext = React.createContext<Nullable<CartContextValue>>(null)

const selectLatestCart = (
  items: ReturnType<typeof useConversation>['items']
): Nullable<ConversationCartComponent> => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]
    if (item.kind !== ConversationItemKinds.ENVELOPE) continue
    const cart = item.components.find(
      (component): component is ConversationCartComponent =>
        component.type === ConversationComponentTypes.CART
    )
    if (cart) return cart
  }
  return null
}

export const CartProvider = ({
  conversationLabel,
  onContinue,
  children,
}: React.PropsWithChildren<{
  conversationLabel: Nullable<string>
  onContinue: () => void
}>) => {
  const { items, status, sendSilentMessage, conversationId } = useConversation()
  const { url } = useAppRoutes()
  const history = useHistory()
  const {
    commands: { setPreferrenceToAIFlow },
  } = useSetOrderRequestFlowPreferencePresenter()
  const { addRecentConversation, removeRecentConversation } =
    useRecentConversations()

  const cart = React.useMemo(() => selectLatestCart(items), [items])

  // A conversation only becomes worth resuming once the agent has produced a
  // cart. Gated on hasCart (not cart identity) so streamed cart updates within
  // a conversation don't rewrite the entry. Resumed conversations carry no
  // label; their recent entry was already refreshed when the user reopened
  // them.
  const hasCart = cart !== null
  React.useEffect(() => {
    if (!hasCart || conversationId === null || conversationLabel === null)
      return
    addRecentConversation({ conversationId, label: conversationLabel })
  }, [hasCart, conversationId, conversationLabel, addRecentConversation])

  const handleContinued = React.useCallback(() => {
    // The preference is only forced once the user commits to the request the
    // conversation built — exploring Guided Intake without continuing must not
    // change their classic/AI choice.
    setPreferrenceToAIFlow()

    if (conversationId !== null) {
      removeRecentConversation(conversationId)
    }

    // The continue turn replaced the draft server-side, so the redirect is
    // flagged for the order request page to drop its cached copy — an unflagged
    // navigation to the same route must never touch that cache.
    const state: OrderRequestRedirectState = { orderDraftReplaced: true }
    history.push(url('OrderRequestPage'), state)
    onContinue()
  }, [
    setPreferrenceToAIFlow,
    conversationId,
    removeRecentConversation,
    history,
    url,
    onContinue,
  ])

  const value = useCart({
    cart,
    status,
    sendSilentMessage,
    onContinued: handleContinued,
  })

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCartContext = (): CartContextValue => {
  const context = React.useContext(CartContext)
  if (!context) {
    throw new Error('useCartContext must be used within a CartProvider')
  }
  return context
}
